import { getSupabaseClient } from './supabase.client.js';

/**
 * Repository of the staff FAQ (`faq_entradas`, `faq_busquedas`, migration 30;
 * openspec/changes/staff-faq/spec.md). Browser-direct under RLS (decision
 * `admin-data-access`): `counter` reads visible entries and inserts searches,
 * `admin` does everything else. Columns are always explicit (never `*`).
 */

const ENTRADA_COLUMNS = 'id, pregunta, en_corto, categoria, ojo, avisa_a, pasos, otras_formas, visible, updated_at';
const BUSQUEDA_COLUMNS = 'id, consulta, tipo, created_at';

/**
 * Most pending searches loaded at once. PostgREST cuts a response at its
 * max-rows without saying so (the problem fichadas.repository.js hit), so the
 * list asks for one more row to know whether it was cut.
 */
const MAX_BUSQUEDAS = 1000;

/** Fields admin may write; `id`, `created_at` and `updated_at` belong to the database. */
const EDITABLE_FIELDS = ['pregunta', 'en_corto', 'categoria', 'ojo', 'avisa_a', 'pasos', 'otras_formas'];

/**
 * Re-throws a Supabase error keeping the Postgres code (same pattern as the
 * other repositories of the panel).
 * @param {{ message: string, code?: string }} error
 * @returns {never}
 */
function rethrow(error) {
    const err = new Error(error.message);
    if (error.code) err.code = error.code;
    throw err;
}

/** @param {Record<string, any>} data */
function editable(data) {
    return Object.fromEntries(EDITABLE_FIELDS.filter((k) => k in data).map((k) => [k, data[k]]));
}

/**
 * Visible entries only — also under an admin session, whose RLS would return
 * hidden rows too: the staff section shows what employees see.
 * @returns {Promise<Array<Record<string, any>>>}
 */
export async function listEntradasVisibles() {
    const client = getSupabaseClient();
    if (!client) return [];
    const { data, error } = await client
        .from('faq_entradas')
        .select(ENTRADA_COLUMNS)
        .eq('visible', true)
        .order('categoria', { ascending: true })
        .order('pregunta', { ascending: true });
    if (error) rethrow(error);
    return data ?? [];
}

/**
 * Every entry, visible and hidden (admin editor).
 * @returns {Promise<Array<Record<string, any>>>}
 */
export async function listEntradas() {
    const client = getSupabaseClient();
    if (!client) return [];
    const { data, error } = await client
        .from('faq_entradas')
        .select(ENTRADA_COLUMNS)
        .order('categoria', { ascending: true })
        .order('pregunta', { ascending: true });
    if (error) rethrow(error);
    return data ?? [];
}

/**
 * @param {Record<string, any>} data
 * @returns {Promise<Record<string, any>>} the created row
 */
export async function crearEntrada(data) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Supabase no está configurado.');
    const { data: row, error } = await client.from('faq_entradas').insert(editable(data)).select(ENTRADA_COLUMNS).single();
    if (error) rethrow(error);
    return row;
}

/**
 * @param {string} id
 * @param {Record<string, any>} data
 * @returns {Promise<Record<string, any>>} the updated row
 */
export async function actualizarEntrada(id, data) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Supabase no está configurado.');
    const { data: row, error } = await client
        .from('faq_entradas')
        .update(editable(data))
        .eq('id', id)
        .select(ENTRADA_COLUMNS)
        .single();
    if (error) rethrow(error);
    return row;
}

/**
 * @param {string} id
 * @param {boolean} visible
 * @returns {Promise<void>}
 */
export async function setEntradaVisible(id, visible) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Supabase no está configurado.');
    const { error } = await client.from('faq_entradas').update({ visible }).eq('id', id);
    if (error) rethrow(error);
}

/**
 * @param {string} id
 * @returns {Promise<void>}
 */
export async function borrarEntrada(id) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Supabase no está configurado.');
    const { error } = await client.from('faq_entradas').delete().eq('id', id);
    if (error) rethrow(error);
}

/**
 * Inserts a failed-search row. No `.select()` after the insert: `counter` has
 * no SELECT policy on `faq_busquedas`, so asking for the row back would fail.
 * Callers go through `staff-faq.service.js#registrarBusqueda`, which validates.
 * @param {{ consulta: string, tipo: 'sin_resultado' | 'no_encontre' }} busqueda
 * @returns {Promise<void>}
 */
export async function insertBusqueda({ consulta, tipo }) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Supabase no está configurado.');
    const { error } = await client.from('faq_busquedas').insert({ consulta, tipo });
    if (error) rethrow(error);
}

/**
 * Searches not yet reviewed, newest first (admin), at most `MAX_BUSQUEDAS`.
 * `truncado` says older pending rows were left out.
 * @returns {Promise<{ filas: Array<{ id: string, consulta: string, tipo: string, created_at: string }>, truncado: boolean }>}
 */
export async function listBusquedasPendientes() {
    const client = getSupabaseClient();
    if (!client) return { filas: [], truncado: false };
    const { data, error } = await client
        .from('faq_busquedas')
        .select(BUSQUEDA_COLUMNS)
        .is('revisada_at', null)
        .order('created_at', { ascending: false })
        .limit(MAX_BUSQUEDAS + 1);
    if (error) rethrow(error);
    const filas = data ?? [];
    return { filas: filas.slice(0, MAX_BUSQUEDAS), truncado: filas.length > MAX_BUSQUEDAS };
}

/**
 * Values per `.in()` filter; the filter travels in the PATCH query string,
 * and a common gateway limit is 8 KB. 100 uuids is about 3.7 KB. A text can
 * be 200 characters, and a 3-byte character (…, “, —, €, typed by phone
 * autocorrect) URL-encodes to 9 bytes: 3 texts is at most about 5.4 KB.
 */
const IN_BATCH_IDS = 100;
const IN_BATCH_TEXTOS = 3;

/**
 * postgrest-js (2.110) wraps an `in()` value in quotes only when it has `,()`
 * and never escapes `"` or `\` inside it, so such a text would match nothing.
 * Those rows are only reachable by id.
 * @param {string} texto
 */
const seguroParaIn = (texto) => !/["\\]/.test(texto);

/** @template T @param {T[]} lista @param {number} tamano @returns {T[][]} */
function enLotes(lista, tamano) {
    const lotes = [];
    for (let i = 0; i < lista.length; i += tamano) lotes.push(lista.slice(i, i + tamano));
    return lotes;
}

/**
 * Marks one group of pending searches as reviewed (B6) and returns how many
 * rows changed.
 * - By id: every loaded row of the group, whatever its text.
 * - By text: pending rows with the same raw text that were beyond the loaded
 *   page (`truncado`), for texts `in()` can carry safely.
 * Only pending rows change, so already reviewed rows keep their `revisada_at`.
 * Zero rows changed is an error (RLS, or already reviewed elsewhere), never a
 * silent success.
 * @param {{ ids: string[], consultas: string[] }} grupo
 * @returns {Promise<number>}
 */
export async function marcarBusquedasRevisadas({ ids, consultas }) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Supabase no está configurado.');
    const ahora = new Date().toISOString();
    let marcadas = 0;
    const filtros = [
        ...enLotes(ids, IN_BATCH_IDS).map((lote) => ['id', lote]),
        ...enLotes(consultas.filter(seguroParaIn), IN_BATCH_TEXTOS).map((lote) => ['consulta', lote]),
    ];
    for (const [columna, lote] of filtros) {
        const { data, error } = await client
            .from('faq_busquedas')
            .update({ revisada_at: ahora })
            .in(columna, lote)
            .is('revisada_at', null)
            .select('id');
        if (error) rethrow(error);
        marcadas += data?.length ?? 0;
    }
    if (marcadas === 0) throw new Error('No se marcó ninguna búsqueda como revisada.');
    return marcadas;
}
